import { Injectable } from '@angular/core';
import { catalogAdapter, containerAdaptor } from 'qp-adaptor';
import {
  CatalogFacadeService,
  CatalogRequest,
  CollectionRequest,
  createCatalogFacadeService,
} from 'qp-facade';
import {
  CatalogVm,
  ContainerList,
  ContainerVm,
  ResourceVm,
  StorefrontData,
} from 'qp-ui-interface';
import { ConfigService } from '../shared/services/config.service';
import { LocalStorageService } from '../shared/services/storage/local-storage.service';
import { KEYS } from '../shared/utils/constants/storage.constants';
import { getDeviceType } from '../shared/utils/deviceUtils';
import { StorefrontPolicyService } from './../shared/services/firstlight/storefront-policy/storefront-policy.service';
import { NewrelicService } from '../analytics/newrelic/services/newrelic.service';

@Injectable({
  providedIn: 'root',
})
export class CatalogService {
  private catalogFacadeService: CatalogFacadeService;
  constructor(
    private configService: ConfigService,
    private policyService: StorefrontPolicyService,
    private localStorage: LocalStorageService,
    private readonly newrelicService: NewrelicService
  ) {
    const CATALOG_SERVICE_END_POINT =
      this.configService.applicationConfig.catalogURL;
    this.catalogFacadeService = createCatalogFacadeService(
      CATALOG_SERVICE_END_POINT
    );
  }

  async getStorefrontData(
    tabId?: string,
    pageSize: number = 3,
    pageNumber: number = 1
  ): Promise<CatalogVm> {
    try {
      const policy = this.policyService.getPolicy();
      const catalogRequest: CatalogRequest = {
        activeCatalogLanguage: `${policy.acl}`,
        deviceType: policy.dt,
        includeVideoGame: false,
        includePromotions: false,
        sfInfo: true,
        regionCode: policy.reg,
        includeTvod: policy.itvod,
        pf: policy.pf,
        pl: policy.pl,
      };
      if (tabId) {
        catalogRequest.tabId = tabId;
        catalogRequest.storefrontId =
          this.localStorage.getItem(KEYS.STOREFRONT_ID) || undefined;
      }
      if (pageSize) {
        catalogRequest.pageSize = pageSize;
      }
      if (pageNumber) {
        catalogRequest.pageNumber = pageNumber;
      }
      const storefrontDataResponse: StorefrontData =
        await this.catalogFacadeService.getCatalogData(catalogRequest);
      const catalogVm: CatalogVm = catalogAdapter(
        storefrontDataResponse,
        this.configService.applicationConfig.imageResizeURL,
        getDeviceType()
      );

      // CRITICAL: Strip unused properties from ResourceVm objects to reduce memory usage
      this.optimizeResourceVmMemory(catalogVm);

      this.localStorage.setItem(KEYS.STOREFRONT_ID, catalogVm.storefrontId);
      return catalogVm;
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      throw error;
    }
  }

  private optimizeResourceVmMemory(catalogVm: CatalogVm): void {
    // CRITICAL: Strip unused properties from all ResourceVm objects to reduce memory usage
    if (catalogVm?.tabVMs) {
      catalogVm.tabVMs.forEach((tabvm) => {
        if (tabvm?.containers) {
          tabvm.containers.forEach((container) => {
            container.resources = container.resources.map((resource) =>
              this.stripUnusedResourceProperties(resource)
            );
          });
        }
      });
    }
  }

  private stripUnusedResourceProperties(resource: ResourceVm): any {
    // CRITICAL: Keep only the essential properties you specified
    const {
      id,
      key,
      name,
      title,
      uniqueId,
      type,
      contentType,
      localizedName,
      shortDescription,
      image,
      is4k,
      dolby,
      advisories,
      contentGenre,
      releaseYear,
      rating,
      runningTime,
      relatedItemsUrl,
      contentUrl,
      licenseUrl,
      freePreview,
      endTime,
      startTime,
      seriesId,
      seriesTitle,
      episodeNumber,
      seasonNumber,
      layout,
      source,
      collectionLayout,
      containerType,
      containers,
      offset,
      storeFrontId,
      tabId,
      tabName,
      collectionID,
      collectionName,
      containerId,
      containerName,
      ratings,
      entitlement,
      localizedCast,
      localizedDirector,
      releaseDate,
      director,
      studio,
      catalog,
      tag,
      isSeries,
      seasonId,
      isPremium,
      tags,
      episodesCount,
      keywords,
      audioQuality,
      videoQuality,
      advisory,
      trayPosition,
      positionInTray,
      contentSku,
      isPurchased,
      purchaseType,
      tvodPurchaseConfiguration,
      priceTag,
      updatedTime,
      isTvod,
      catalogAvailabilityEndDate,
      catalogAvailabilityStartDate,
      apUrl,
      ape,
      runningTimeinMins,
      heroTitleImage,
      customTitleImage,
      totalNoOfSeasons,
      heroTags,
      customTags,
      bannerImage,
      collectionUrl,
      trailers,
      subtitle,
      providerImage,
      providerName,
      network,
      skips,
      collectionType,
      multiLingualAudio,
      inLanguage,
    } = resource;
    let updatedCollectionImage = null;
    if (collectionType && image) {
      try {
        // Parse URL properly
        const url = new URL(image);
        url.searchParams.set('width', '960');
        updatedCollectionImage = url.toString();
      } catch (e) {
        // Fallback: if URL parsing fails, use regex replace
        updatedCollectionImage = image.replace(/width=305/g, 'width=960');
      }
    }
    return {
      id,
      key,
      name,
      title,
      uniqueId,
      type,
      contentType,
      localizedName,
      shortDescription,
      subtitle,
      image: updatedCollectionImage || image,
      is4k,
      dolby,
      advisories,
      contentGenre,
      releaseYear,
      rating,
      runningTime,
      relatedItemsUrl,
      contentUrl,
      licenseUrl,
      freePreview,
      endTime,
      startTime,
      seriesId,
      seriesTitle,
      episodeNumber,
      seasonNumber,
      layout,
      source,
      collectionLayout,
      containerType,
      containers,
      offset,
      storeFrontId,
      tabId,
      tabName,
      collectionID,
      collectionName,
      containerId,
      containerName,
      ratings,
      entitlement,
      localizedCast,
      localizedDirector,
      releaseDate,
      director,
      studio,
      catalog,
      tag,
      isSeries,
      seasonId,
      isPremium,
      tags,
      episodesCount,
      keywords: keywords || [],
      audioQuality,
      videoQuality,
      advisory,
      trailers,
      trayPosition,
      positionInTray,
      contentSku,
      isPurchased,
      purchaseType,
      tvodPurchaseConfiguration,
      priceTag,
      updatedTime,
      isTvod,
      catalogAvailabilityEndDate,
      catalogAvailabilityStartDate,
      apUrl,
      ape,
      runningTimeinMins,
      heroTitleImage,
      customTitleImage,
      totalNoOfSeasons,
      heroTags,
      customTags,
      bannerImage,
      collectionUrl,
      providerImage,
      providerName,
      network,
      skips,
      collectionType,
      multiLingualAudio,
      inLanguage,
    };
  }

  async getCollectionStorefrontData(
    collectionId: string,
    pageSize: number = 3,
    pageNumber: number = 1
  ): Promise<{ totalContainers: number; containers: ContainerVm[] }> {
    const policy = this.policyService.getPolicy();
    const catalogRequest: CollectionRequest = {
      activeCatalogLanguage: `${policy.acl}`,
      deviceType: policy.dt,
      regionCode: policy.reg,
      pf: policy.pf,
      collectionId,
      pageNumber,
      pageSize,
      pl: policy.pl,
    };

    const response: ContainerList =
      await this.catalogFacadeService.getCatalogCollectionData(catalogRequest);

    return {
      containers: response.containers.map((container) =>
        containerAdaptor(
          this.configService.applicationConfig.imageResizeURL,
          container,
          getDeviceType()
        )
      ),
      totalContainers: response.totalContainers,
    };
  }
  catch(error) {
    this.newrelicService.sendErrorEvent({
      error,
    });
    throw error;
  }
}
